import { Radio, Space, Table, Modal, Button, Tooltip, Form, DatePicker, Input } from 'antd';
import { DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { Granule } from '../../types/Granule';
import { deleteGranules, getGranules } from '../../services/GranuleService';
import './GranulesTable.css'

const { RangePicker } = DatePicker;

type GranulesTableProps = {
  kapsuleId: string;
};

type GranulesTableFilter = 'ALL' | 'RELOADED' | 'NOT_RELOADED';

const filterOptions: Array<{ label: string; value: GranulesTableFilter }> = [
  { label: 'All', value: 'ALL' },
  { label: 'Reloaded', value: 'RELOADED' },
  { label: 'Not Reloaded', value: 'NOT_RELOADED' },
];

export const GranulesTable: FunctionComponent<GranulesTableProps> = ({kapsuleId}) => {
  const [filter, setFilter] = useState<GranulesTableFilter>('ALL');
  const [searchText, setSearchText] = useState<string>('');
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [selectedKapsuleChildIds, setSelectedKapsuleChildIds] = useState<string[]>([]);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [pageTotal, setPageTotal] = useState<number>(0);
  const [orderColumn, setOrderColumn] = useState<string>('');
  const [orderNum, setOrderNum] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');

  const [granules, setGranules] = useState<Granule[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      // debounce
      reloadGranules(kapsuleId, currentPage, pageSize, filter, searchText, orderColumn, orderNum, startTime, endTime)
    }, 500);
    return () => clearTimeout(timer)
  }, [kapsuleId, currentPage, pageSize, filter, searchText, orderColumn, orderNum, startTime, endTime]);

  const reloadGranules = async (kapsuleId:string, currentPage:number, pageSize:number, filter: GranulesTableFilter, searchText: string, orderColumn: string, orderNum: string, startTime: string, endTime: string) => {
    try {
      setTableLoading(true);
      const granules = await getGranules(kapsuleId, currentPage, pageSize, filter, searchText, orderColumn, orderNum, startTime, endTime);
      setGranules(granules.data);
      setPageTotal(granules.total)
    } catch (e) {
      setGranules([]);
      setPageTotal(0)
    } finally {
      setTableLoading(false)
    }
  };

  const columns: ColumnsType<Granule> = [
    { title: 'User', dataIndex: 'user',
      sorter: (a: Granule, b: Granule) => a.user.localeCompare(b.user)
    },    
    { title: 'ID', dataIndex: '_id',
      sorter: (a: Granule, b: Granule) => a._id.localeCompare(b._id),
      render: (text) => {
        return <Tooltip placement="top" title={text}><div style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{text}</div></Tooltip>
     }
    },
    { title: 'Key Value', dataIndex: 'key',
      sorter: (a: Granule, b: Granule) => {
        let akey = a.key;
        let bkey = b.key;
        if (akey === null) akey = ''
        if (bkey === null) bkey = ''
        return akey.toString().localeCompare(bkey.toString())
      }
    },
    { title: 'Last Update', dataIndex: 'updatedAt',
      sorter: (a: Granule, b: Granule) => {
        const aTime = new Date(a.updatedAt).getTime();
        const bTime = new Date(b.updatedAt).getTime();
        return aTime - bTime;
      }
    },
    {
      title: 'Action',
      dataIndex: '',
      key: 'x',
      render: (_text: string, record: Granule) => (
        <Space size="middle">
          <DeleteOutlined
            style={{ color: '#1890ff' }}
            onClick={() => {
              Modal.confirm({
                title: 'Confirm',
                content: `Are you sure to delete ${record._id}?`,
                okText: 'Yes',
                cancelText: 'Cancel',
                onOk: async () => {
                  await deleteGranules([record._id]);
                  reloadGranules(kapsuleId, currentPage, pageSize, filter, searchText, orderColumn, orderNum, startTime, endTime);
                },
              });
            }}
          />
        </Space>
      ),
    },
  ];

  const handleFilterChange = (value?: GranulesTableFilter) => {
    if (value) {
      setFilter(value);
    }
  };

  const rowSelection = {
    onChange: (_selectedRowKeys: React.Key[], selectedRows: Granule[]) => {
      const selectedKeyIds = selectedRows.map((item) => item._id);
      setSelectedKapsuleChildIds(selectedKeyIds);
    }
  };

  const handleTableChange = (pagination:any, filters:any, sorter:any) => {
    setCurrentPage(pagination.current > 0 ? pagination.current : 1)
    setPageSize(pagination.pageSize)
    if (sorter.order) {
      setOrderColumn(sorter.field)
      setOrderNum(sorter.order === 'ascend' ? '1' : '-1')
    } else {
      setOrderColumn('')
      setOrderNum('')
    }
  };

  const onSearchFinish = (fieldsValue: any) => {
    const {search_date_picker, search_text} = fieldsValue;
    setCurrentPage(1)
    
    if (search_date_picker && search_date_picker.length >= 2) {
      setStartTime(search_date_picker[0].format('YYYY-MM-DD'))
      setEndTime(search_date_picker[1].format('YYYY-MM-DD'))
    } else {
      setStartTime('')
      setEndTime('')
    }

    if (search_text && search_text.trim()) {
      setSearchText(search_text.trim());
    } else {
      setSearchText('')
    }
  }

  return (
    <Table
      className='granulesTable'
      loading={tableLoading}
      scroll={{scrollToFirstRowOnChange: true, y: 'calc(100vh - 425px)'}}
      title={() => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <b>Granules</b>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{marginRight: 10}}>
              <Radio.Group
                options={filterOptions}
                onChange={({ target: { value } }: { target: { value?: GranulesTableFilter } }) =>
                  handleFilterChange(value)
                }
                value={filter}
                optionType="button"
                buttonStyle="solid"
              />
              <Button 
                  disabled={selectedKapsuleChildIds.length === 0}
                  type="primary"
                  onClick={() => {
                    Modal.confirm({
                      title: 'Confirm',
                      content: `Are you sure to delete selected Kapsules?`,
                      okText: 'Yes',
                      cancelText: 'Cancel',
                      onOk: async () => {
                        /**执行批量删除 */
                        await deleteGranules(selectedKapsuleChildIds);
                        /**将缓存ids置为空 */
                        setSelectedKapsuleChildIds([]);
                        /**重新reload数据 */
                        reloadGranules(kapsuleId, currentPage, pageSize, filter, searchText, orderColumn, orderNum, startTime, endTime);
                      },
                    });
                  }}
                >
                Delete Selection
              </Button>
            </div>
            <div>
              <Form name="search_form" onFinish={onSearchFinish} layout='inline'>
                <Form.Item name="search_date_picker" style={{marginRight: 5}}>
                  <RangePicker />
                </Form.Item>
                <Form.Item name="search_text">
                  <Input placeholder="input search text" allowClear />
                </Form.Item>
                <Form.Item style={{marginRight: 0}}>
                  <Button type="primary" htmlType="submit" shape="circle" icon={<SearchOutlined />} />
                </Form.Item>
              </Form>
            </div>
          </div>
        </div>
      )}
      rowKey={(record) => record._id}
      columns={columns}
      rowSelection={{ ...rowSelection }}
      dataSource={granules}
      expandable={{
        expandedRowRender: (record) => {
          const text = JSON.stringify(record.data, undefined, 4);
          return (
            <pre>
              <code>{text}</code>
            </pre>
          );
        },
        rowExpandable: (record) => !!record.data,
      }}
      onChange={handleTableChange}
      pagination={{
        current: currentPage,
        pageSize: pageSize,
        pageSizeOptions: ['10', '100', '500', '1000'],
        showQuickJumper: true,
        showSizeChanger: true,
        showTotal: (total) => `Total:${total}`,
        total: pageTotal
      }}
    />
  );
};
